import { ChevronDown, Lightbulb, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useClickOutside } from "../../../hooks/useClickOutside";
import { cn } from "../../../lib/utils";
import { JanIcon } from "../../jan/components/atoms/JanIcon";
import { SuggestedPromptChips } from "../../jan/components/molecules/SuggestedPromptChips";
import type { PublicChatMessage } from "../api/publicChat";
import type { PublicChatStatus } from "../hooks/usePublicChatMachine";
import { usePublicChatScroll } from "../hooks/usePublicChatScroll";
import { PublicChatInput } from "./PublicChatInput";
import { PublicMessageBubble } from "./PublicMessageBubble";
import {
  PUBLIC_SUGGESTED_PROMPTS,
  PublicSuggestedPrompts,
} from "./PublicSuggestedPrompts";

interface Props {
  status: PublicChatStatus;
  messages: PublicChatMessage[];
  streamingContent: string;
  error: string | null;
  onSubmit: (message: string) => void | Promise<void>;
  verifying?: boolean;
  verificationMessage?: string;
  verificationAction?: { label: string; onClick: () => void } | null;
  welcome?: string;
  starters?: string[];
  showMessageActions?: boolean;
  /** Fill the container height (h-full) instead of the viewport (h-screen).
   *  The embed sits inside a fixed-height host element; /jan-demo is full-page. */
  embedded?: boolean;
}

export const PublicChatShell = ({
  status,
  messages,
  streamingContent,
  error,
  onSubmit,
  verifying = false,
  verificationMessage,
  verificationAction,
  welcome,
  starters,
  showMessageActions = true,
  embedded = false,
}: Props) => {
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isStreaming = status === "streaming";
  const isLoading =
    verifying || status === "starting-session" || status === "loading-history";
  const inputDisabled = isStreaming || isLoading;
  const loaderMessage = verificationMessage ?? "Starting demo session…";

  // Index of the most recent user message, so we can anchor it to the top of
  // the viewport (the ChatGPT/Claude-style read-from-the-top behavior the
  // in-app Jan uses) instead of auto-scrolling the stream to the bottom.
  const lastUserIndex = messages.reduce(
    (acc, m, i) => (m.role === "user" ? i : acc),
    -1
  );
  const {
    scrollRef,
    lastUserMessageRef,
    streamingEndRef,
    spacerHeight,
    showSpacer,
    showScrollToBottom,
    scrollToBottom,
    onScroll,
  } = usePublicChatScroll({
    messageCount: messages.length,
    lastMessageRole: messages[messages.length - 1]?.role,
    isStreaming,
    streamingContent,
  });

  const empty = messages.length === 0 && !isStreaming;

  useClickOutside(
    [suggestionsRef, toggleBtnRef],
    () => setShowSuggestions(false),
    showSuggestions
  );

  useEffect(() => {
    if (empty && showSuggestions) setShowSuggestions(false);
  }, [empty, showSuggestions]);

  // Variant starters (embed) drive both the empty-state chips and the popover;
  // fall back to the generic prompts for /jan-demo.
  const variantPrompts =
    starters && starters.length > 0
      ? starters.map((s, i) => ({
          promptName: `starter-${i}`,
          displayLabel: s,
          order: i + 1,
        }))
      : null;

  return (
    <div
      className={cn("flex flex-col bg-white", embedded ? "h-full" : "h-screen")}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <JanIcon size="md" />
          <div>
            <h1 className="text-base font-semibold text-slate-800">
              Just Ask NovoNote
            </h1>
            <p className="text-xs text-slate-500">
              Public demo · NovoPsych's clinical AI assistant
            </p>
          </div>
        </div>
        <a
          href="https://novopsych.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-slate-700 hover:text-slate-900 transition-colors"
        >
          Sign up at NovoPsych →
        </a>
      </header>

      <div className="relative flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-full overflow-y-auto"
        >
          <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-4">
            {isLoading ? (
              <div className="flex flex-col items-center gap-3 text-slate-500 mt-16">
                <div
                  className="w-8 h-8 border-2 border-slate-300 rounded-full animate-spin"
                  style={{ borderTopColor: "#05BDFF" }}
                />
                <p className="text-sm">{loaderMessage}</p>
                {verificationAction ? (
                  <button
                    type="button"
                    onClick={verificationAction.onClick}
                    className="text-xs font-medium text-slate-700 hover:text-slate-900 transition-colors underline-offset-2 hover:underline"
                  >
                    {verificationAction.label}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!isLoading && empty ? (
              <div className="flex flex-col items-center gap-6 mt-12">
                <div className="text-center">
                  <JanIcon size="xl" className="mx-auto mb-3" />
                  <h2 className="text-lg font-semibold text-slate-800">
                    Hi, I'm Jan.
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {welcome ??
                      "Ask me anything about psychometric assessments or NovoPsych."}
                  </p>
                </div>
                {variantPrompts ? (
                  <SuggestedPromptChips
                    prompts={variantPrompts}
                    on_click={(p) => void onSubmit(p.displayLabel)}
                    className="max-w-2xl mx-auto"
                  />
                ) : (
                  <PublicSuggestedPrompts onPick={onSubmit} />
                )}
              </div>
            ) : null}

            {!isLoading
              ? messages.map((m, i) => (
                  <div
                    key={m.id}
                    ref={i === lastUserIndex ? lastUserMessageRef : undefined}
                  >
                    <PublicMessageBubble
                      message={m}
                      showActions={showMessageActions}
                    />
                  </div>
                ))
              : null}

            {isStreaming && streamingContent ? (
              <PublicMessageBubble
                message={{
                  id: "__streaming__",
                  role: "assistant",
                  content: streamingContent,
                  created_at: new Date().toISOString(),
                }}
                isStreaming
              />
            ) : null}

            {isStreaming && !streamingContent ? (
              <div className="flex gap-3 py-4">
                <div className="flex-shrink-0 mt-1">
                  <JanIcon size="md" />
                </div>
                <div className="inline-block px-4 py-3 rounded-2xl rounded-bl-md bg-slate-100">
                  <span className="inline-flex gap-1 text-slate-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse [animation-delay:200ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse [animation-delay:400ms]" />
                  </span>
                </div>
              </div>
            ) : null}

            {/* End-of-content marker (before the spacer) so "scroll to latest"
              lands on the answer, not the empty spacer. */}
            {isStreaming ? (
              <div ref={streamingEndRef} className="h-0" aria-hidden="true" />
            ) : null}

            {error ? (
              <div className="mx-auto max-w-md text-center mt-8">
                <div className="text-4xl mb-3">😔</div>
                <h3 className="text-base font-medium text-slate-700 mb-1">
                  Something went wrong
                </h3>
                <p className="text-sm text-slate-500">{error}</p>
              </div>
            ) : null}

            {/* Bottom spacer gives the latest question room to reach the top.
                Sized to the scroll viewport; removed once the answer is in. */}
            {showSpacer ? (
              <div
                aria-hidden="true"
                data-testid="jan-scroll-spacer"
                style={{ height: spacerHeight }}
              />
            ) : null}
          </div>
        </div>

        {showScrollToBottom ? (
          <button
            type="button"
            onClick={scrollToBottom}
            aria-label="Scroll to latest"
            className={cn(
              "absolute bottom-4 left-1/2 -translate-x-1/2 z-10",
              "flex items-center justify-center w-9 h-9 rounded-full",
              "bg-white border border-slate-200 shadow-md text-slate-600",
              "hover:text-slate-900 hover:bg-slate-50 transition-colors"
            )}
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        ) : null}
      </div>

      {!isLoading ? (
        <div className="border-t border-slate-200 px-4 py-3">
          <div className="relative mx-auto w-full max-w-3xl">
            {showSuggestions && !empty ? (
              <div
                ref={suggestionsRef}
                className={cn(
                  "absolute bottom-full left-0 right-0 mb-2 p-4 bg-white rounded-xl",
                  "border border-slate-200 shadow-lg",
                  "animate-in fade-in slide-in-from-bottom-2 duration-200"
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Suggested prompts
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(false)}
                    className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors"
                    aria-label="Close suggestions"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <SuggestedPromptChips
                  prompts={variantPrompts ?? PUBLIC_SUGGESTED_PROMPTS}
                  on_click={(p) => {
                    setShowSuggestions(false);
                    void onSubmit(p.displayLabel);
                  }}
                />
              </div>
            ) : null}

            <div className="flex items-end gap-2">
              {!empty ? (
                <button
                  ref={toggleBtnRef}
                  type="button"
                  onClick={() => setShowSuggestions((prev) => !prev)}
                  className={cn(
                    "flex-shrink-0 p-2 rounded-lg transition-colors mb-0.5",
                    showSuggestions
                      ? "bg-amber-100 text-amber-600"
                      : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  )}
                  aria-label="Show suggested prompts"
                  title="Suggested prompts"
                >
                  <Lightbulb className="w-5 h-5" />
                </button>
              ) : null}
              <div className="flex-1 min-w-0">
                <PublicChatInput
                  onSubmit={onSubmit}
                  disabled={inputDisabled}
                  isProcessing={isStreaming}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">
              AI makes mistakes. Verify information.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};
